import { useTheme } from '@mui/material/styles';
import Box, { type BoxProps } from '@mui/material/Box';
import { mergeSx } from '../sx';

export interface IconProps extends Omit<BoxProps<'span'>, 'children'> {
  /** Material Symbols Rounded glyph name, e.g. "space_dashboard". */
  name: string;
  size?: number;
  fill?: boolean;
  /** Flip horizontally in RTL (chevrons, arrows). */
  mirrorInRtl?: boolean;
}

export function Icon({
  name,
  size = 20,
  fill = false,
  mirrorInRtl = false,
  sx,
  className,
  ...rest
}: IconProps) {
  const theme = useTheme();
  const flip = mirrorInRtl && theme.direction === 'rtl';
  return (
    <Box
      component="span"
      aria-hidden
      // Merge: MUI clones icons (Chip, Autocomplete…) with its own className, which must not drop the font class.
      className={className ? `material-symbols-rounded ${className}` : 'material-symbols-rounded'}
      sx={mergeSx(
        {
          // The emotion cache is prepended, so material-symbols' own `font-size: 24px` would win on equal
          // specificity: raise it with the font class.
          '&.material-symbols-rounded': { fontSize: size },
          lineHeight: 1,
          width: size,
          height: size,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          flexShrink: 0,
          userSelect: 'none',
          fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 400, 'GRAD' 0, 'opsz' ${Math.min(48, Math.max(20, size))}`,
          transform: flip ? 'scaleX(-1)' : undefined,
        },
        sx,
      )}
      {...rest}
    >
      {name}
    </Box>
  );
}
